// TODO(#69): Technical debt
// Currently, PubNub response values are not strictly typed.
// To improve type safety and maintainability, PubNub type definitions should be organized and addressed in the future.
import { patchChannelResponseStatus } from '../../utils/channelResponseStatus';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { FileEvent, MessageEvent, SignalEvent } from 'pubnub';
import { usePubNub } from 'pubnub-react';
import React, { useEffect, useState } from 'react';
import {
  useOrganizationBusy,
  useOrganizationScopeOrNull,
} from '../OrganizationScope/context';
import { getQueryOrganizationId } from '../../configs/queryClient';
import { useOrganizationToast } from '../../hooks/useOrganizationToast';
import {
  updateInquiryChannelActive,
  updateInquirySeenTime,
} from '../../api/inquiryChat';
import {
  TChannelResponseStatus,
  TChannelParticipant,
  updateChannelActive,
  updateSeenTime,
  notifyLine,
  deleteChannelMessage,
} from '../../api/messages';
import {
  CHAT_MESSAGE_SIZE,
  CHAT_SEND_ACTIVE_DEBOUNCE_MS,
} from '../../constants';
import { EOrganizationMemberRole } from 'types/enum';
import { commonStrings, messageStrings } from '../../constants/strings';
import { useMessageStore } from '../../store';
import { AiHistoryModal } from './AiHistoryModal';
import { AiSuggestionModal } from './AiSuggestionModal';
import { ChatHeader } from './ChatHeader';
import { MessageInput } from './MessageInput';
import { MessageList } from './MessageList';
import { useCurrentUser, useInquiryAiSuggestions, useUploadFile } from 'hooks';
import {
  CHAT_FILE_SENT_LABEL,
  getChatFileKind,
  isS3ChatFileKind,
} from './constants';
import { TOTAL_UNREAD_COUNT_QUERY_KEY } from '../../hooks/useTotalUnreadCount';
import { ConfirmModal } from '../ConfirmModal';
import { normalizePubNubTimetoken } from '../../utils/pubnubTimetoken';
import { flattenPubNubFileMessage } from '../../utils/pubnubHistoryMessage';
import {
  RECALL_MESSAGE_EVENT,
  getTrustedRecallMessageNotification,
  isRecallMessageNotification,
  isRelevantRecallForChat,
  isMessageRecalledByPubNub,
  isPubNubMessageDeletedAction,
  markMessageRecalled,
} from '../../utils/chatRecallMessage';

const SIGNAL_EVENT = {
  TYPING_ON: 'TYPING_ON',
  TYPING_OFF: 'TYPING_OFF',
  RECALL_MESSAGE: RECALL_MESSAGE_EVENT,
};

type MessageDetail = {
  channel: string;
  message: {
    text?: string;
    // PubNub sendFile のファイル参照（getFileUrl に渡す）
    file?: { id: string; name: string };
    media?: Array<{
      type?: string;
      url?: string;
      fileName?: string;
      originalHeight?: number;
      originalWidth?: number;
      poster?: string;
    }>;
  };
  timetoken: string | number;
  uuid?: string;
  url?: string;
  recalled?: boolean;
  actions?: Record<string, Record<string, unknown>>;
};

type Props = {
  channel: string;
  participants: TChannelParticipant[];
  loading?: boolean;
  enableAi?: boolean;
  skipLineNotify?: boolean;
  inquiryChatMode?: boolean;
  onBack?: () => void;
  onMessageRecalled?: (channelId: string) => void;
  responseStatus?: TChannelResponseStatus | null;
  onChangeResponseStatus?: (status: TChannelResponseStatus) => void;
  isUpdatingResponseStatus?: boolean;
};

export function Chat({
  channel,
  participants,
  loading = false,
  enableAi: enableAiProp = false,
  skipLineNotify = false,
  inquiryChatMode = false,
  onBack,
  onMessageRecalled,
  responseStatus,
  onChangeResponseStatus,
  isUpdatingResponseStatus,
}: Props) {
  const pubnub = usePubNub();
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const { uploadToS3 } = useUploadFile();
  // PubNub直接送信・取り消しは react-query を経由しないため busy 登録する
  const { beginBusy, endBusy } = useOrganizationBusy();
  // debounce 中の PATCH が切り替え後の組織ヘッダーで飛ばないよう組織を固定する
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;
  const { toastSuccess, toastError } = useOrganizationToast();
  const { adminAccounts } = useMessageStore();
  const timeoutRef = React.useRef<NodeJS.Timeout>();
  const refetchUnreadCount = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: TOTAL_UNREAD_COUNT_QUERY_KEY });
  }, [queryClient]);

  const refetchUnreadCountRef = React.useRef(refetchUnreadCount);
  refetchUnreadCountRef.current = refetchUnreadCount;

  const [memberIsTyping, setMemberIsTyping] = useState<string[]>([]);
  const [messages, setMessages] = useState<MessageDetail[]>([]);
  const [deleteTargetTimetoken, setDeleteTargetTimetoken] = useState<
    string | null
  >(null);
  const [isRecallingMessage, setIsRecallingMessage] = useState(false);
  const recalledTimetokensRef = React.useRef<Set<string>>(new Set());

  const subscribeChannels = React.useMemo(
    () =>
      [channel, currentUser?.uuid].filter((value): value is string => !!value),
    [channel, currentUser?.uuid],
  );

  const enableAi =
    enableAiProp && currentUser?.role === EOrganizationMemberRole.ADMIN;

  const inquiryAi = useInquiryAiSuggestions(channel);

  // Create members map from participants
  // Map by UUID - ensure UUID matches with message UUID from PubNub
  // IMPORTANT FOR BE: UUID in participants must match UUID in PubNub messages
  // - When male user sends message, PubNub uses their UUID (from PubNub init in male app)
  // - BE must return the same UUID in participants array
  // - UUID format must be consistent (no extra spaces, same case, etc.)
  const members = React.useMemo(() => {
    const map: Record<string, TChannelParticipant> = {};
    participants.forEach((p) => {
      if (p.uuid) {
        map[p.uuid] = p;
      } else if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ [MISSING UUID] Participant without UUID:', {
          id: p.id,
          name: p.name,
          userMaleId: 'userLineId' in p ? p.userLineId : undefined,
          organizationMemberId:
            'organizationMemberId' in p ? p.organizationMemberId : undefined,
          message:
            'BE should return UUID for all participants to match PubNub message UUID',
        });
      }
    });
    return map;
  }, [participants]);

  // Mutations
  const { mutate } = useMutation({
    mutationFn: () => {
      return inquiryChatMode
        ? updateInquiryChannelActive(channel, organizationId)
        : updateChannelActive(channel, organizationId);
    },
    retry: 0,
    onSuccess: () => {
      // 戻る/進むでは新しい組織の render の後にアンマウント時の記録が飛ぶため、表示していた組織と比べる
      if (!inquiryChatMode && organizationId === getQueryOrganizationId()) {
        patchChannelResponseStatus(queryClient, channel, 'RESPONDED');
      }
      refetchUnreadCountRef.current();
    },
  });
  const { mutateAsync: mutateSeen } = useMutation({
    mutationFn: () => {
      return inquiryChatMode
        ? updateInquirySeenTime(channel, organizationId)
        : updateSeenTime(channel, organizationId);
    },
    retry: 0,
    onSuccess: () => {
      refetchUnreadCountRef.current();
    },
  });

  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['message-list', channel],
    queryFn: async () => {
      return pubnub
        .fetchMessages({
          channels: [channel],
          count: CHAT_MESSAGE_SIZE,
          includeMessageType: true,
          includeUUID: true,
          includeMeta: true,
          includeMessageActions: true,
          start: messages[messages.length - 1]?.timetoken,
        })
        .then((res) => res.channels[channel] || []);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.length >= CHAT_MESSAGE_SIZE
        ? lastPageParam + 1
        : undefined;
    },
    gcTime: 0,
    enabled: !!pubnub && !loading,
  });

  const mapMessage = React.useCallback(
    (message: any): MessageDetail => {
      // 履歴API(fetchMessages)の file メッセージは本文が入れ子で返り、そのままだと
      // MessageItem が text を解決できないため、リアルタイム受信(handleFile)と同じ
      // フラット形へ正規化してから url / text を扱う
      const normalizedMessage = flattenPubNubFileMessage(
        message.message,
      ) as MessageDetail['message'];

      let url;
      // Handle PubNub file (from sendFile)
      if (normalizedMessage?.file) {
        url = pubnub.getFileUrl({
          channel: channel,
          id: normalizedMessage.file.id,
          name: normalizedMessage.file.name,
        });
      }
      // Handle media array (from backend sendMessageFromAdmin)
      // If media array exists, use first media URL
      else if (
        normalizedMessage?.media &&
        Array.isArray(normalizedMessage.media) &&
        normalizedMessage.media.length > 0
      ) {
        url = normalizedMessage.media[0].url;
      }
      return {
        ...message,
        message: normalizedMessage,
        timetoken: normalizePubNubTimetoken(message.timetoken),
        url: url,
        recalled:
          isMessageRecalledByPubNub(message.actions) ||
          recalledTimetokensRef.current.has(
            normalizePubNubTimetoken(message.timetoken),
          ),
        actions: message.actions,
      };
    },
    [pubnub, channel],
  );

  const handleSignal = React.useCallback(
    (params: SignalEvent) => {
      if (params.channel !== channel) return;

      const recallNotification = getTrustedRecallMessageNotification(
        params.message,
        params.publisher,
      );
      if (recallNotification) {
        markMessageRecalled(
          recalledTimetokensRef.current,
          setMessages,
          recallNotification.timetoken!,
        );
        return;
      }

      const signalMessage = params.message;
      const signalEvent =
        typeof signalMessage === 'object' && signalMessage?.event
          ? signalMessage.event
          : signalMessage;

      const isMySignal = currentUser?.uuid === params.publisher;
      if (isMySignal) return;

      switch (signalEvent) {
        case SIGNAL_EVENT.TYPING_ON:
          setMemberIsTyping((prev) =>
            prev.some((uuid) => uuid === params.publisher)
              ? prev
              : [...prev, params.publisher],
          );
          break;
        case SIGNAL_EVENT.TYPING_OFF:
          setMemberIsTyping((prev) =>
            prev.filter((uuid) => uuid !== params.publisher),
          );
          break;
        default:
          break;
      }
    },
    [currentUser?.uuid, channel],
  );

  const handleMessage = React.useCallback(
    (event: MessageEvent) => {
      const recallNotification = getTrustedRecallMessageNotification(
        event.message,
        event.publisher,
      );
      if (
        recallNotification &&
        isRelevantRecallForChat({
          notification: recallNotification,
          eventChannel: event.channel,
          chatChannel: channel,
          currentUserUuid: currentUser?.uuid,
        })
      ) {
        markMessageRecalled(
          recalledTimetokensRef.current,
          setMessages,
          recallNotification.timetoken!,
        );
        return;
      }

      if (channel === event.channel) {
        setMessages((messages) => {
          const eventTimetoken = normalizePubNubTimetoken(event.timetoken);
          return messages.some(
            (message) =>
              eventTimetoken === normalizePubNubTimetoken(message.timetoken),
          )
            ? messages
            : [
                mapMessage({
                  channel: event.channel,
                  message: event.message,
                  timetoken: eventTimetoken,
                  uuid: event.publisher,
                }),
                ...messages,
              ];
        });
      }
    },
    [setMessages, mapMessage, channel, currentUser?.uuid],
  );

  const handleFile = React.useCallback(
    (event: FileEvent) => {
      if (channel === event.channel) {
        const eventTimetoken = normalizePubNubTimetoken(event.timetoken);
        setMessages((messages) => [
          mapMessage({
            channel: event.channel,
            message: { text: event.message.text, file: event.file },
            timetoken: eventTimetoken,
            uuid: event.publisher,
          }),
          ...messages,
        ]);
      }
    },
    [setMessages, mapMessage, channel],
  );

  const handleMessageAction = React.useCallback(
    (event: {
      channel: string;
      event?: string;
      data?: { type?: string };
      messageTimetoken?: string | number;
    }) => {
      if (event.channel !== channel) return;
      if (!isPubNubMessageDeletedAction(event)) return;

      markMessageRecalled(
        recalledTimetokensRef.current,
        setMessages,
        event.messageTimetoken,
      );
    },
    [channel, setMessages],
  );

  const sendMessage = React.useCallback(
    async (message: string, file?: File) => {
      if (!file && !message.trim()) return;
      beginBusy();
      // 送信本体(publish/sendFile)まで完了したか。完了前に失敗した場合は再throwして、
      // MessageInput 側が入力・添付・下書きを保持できるようにする
      let delivered = false;
      try {
        const fileKind = file ? getChatFileKind(file.type) : undefined;
        const messagePayload = {
          text: message,
        };

        let publishResult: { timetoken?: string | number };
        if (file && isS3ChatFileKind(fileKind)) {
          // PDF/動画/ZIP は PubNub sendFile のサイズ上限を超えるため S3 経由で送る
          const [uploaded] = await uploadToS3({ files: [file] });
          if (!uploaded?.uploadedUrl) {
            throw new Error(messageStrings.uploadFailed);
          }
          publishResult = await pubnub.publish({
            channel: channel,
            message: {
              ...messagePayload,
              media: [
                {
                  type: file.type,
                  url: uploaded.uploadedUrl,
                  fileName: file.name,
                  originalHeight: 0,
                  originalWidth: 0,
                },
              ],
            },
          });
        } else if (file) {
          publishResult = await pubnub.sendFile({
            channel: channel,
            message: messagePayload,
            file: file,
          });
        } else {
          publishResult = await pubnub.publish({
            channel: channel,
            message: messagePayload,
          });
        }
        delivered = true;

        if (!skipLineNotify) {
          // timetokenが取れない異常時もbackendのサーバ側解決に委ね、通知は落とさない
          const messageTimetoken =
            normalizePubNubTimetoken(publishResult.timetoken) || undefined;
          notifyLine(channel, messageTimetoken, organizationId).catch((err) => {
            // 通知欠落は本文送信成功と見分けが付かないため、必ず可視化する
            Sentry.captureException(err, {
              tags: { feature: 'chat-notify-line' },
              extra: { channelId: channel },
            });
            toastError(messageStrings.notifyLineFailed);
          });
        }

        // Publish to user channels
        const channelUsers = participants
          .map((item) => item.uuid)
          .filter(Boolean);
        if (currentUser?.uuid) {
          channelUsers.push(currentUser.uuid);
        }

        if (channelUsers.length > 0) {
          Promise.all(
            channelUsers.map((uuid) =>
              pubnub.publish({
                channel: uuid!,
                message: {
                  channel: channel,
                  text: message || CHAT_FILE_SENT_LABEL[fileKind ?? 'image'],
                },
              }),
            ),
          );
        }

        // Update channel active
        if (timeoutRef.current) return;
        timeoutRef.current = setTimeout(() => {
          mutate();
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }, CHAT_SEND_ACTIVE_DEBOUNCE_MS);
      } catch (error) {
        toastError(error);
        // 送信本体の失敗だけ呼び出し元へ伝える（送信後の通知系の失敗は従来どおり握りつぶす）
        if (!delivered) throw error;
      } finally {
        endBusy();
      }
    },
    [
      beginBusy,
      endBusy,
      pubnub,
      channel,
      participants,
      mutate,
      currentUser?.uuid,
      skipLineNotify,
      uploadToS3,
      organizationId,
      toastError,
    ],
  );

  const handleDeleteRequest = React.useCallback((timetoken: string) => {
    setDeleteTargetTimetoken(timetoken);
  }, []);

  const handleConfirmRecall = React.useCallback(async () => {
    if (!deleteTargetTimetoken) return;

    setIsRecallingMessage(true);
    beginBusy();
    const timetoken = normalizePubNubTimetoken(deleteTargetTimetoken);
    try {
      await deleteChannelMessage(channel, timetoken);
      markMessageRecalled(recalledTimetokensRef.current, setMessages, timetoken);
      await queryClient.removeQueries({ queryKey: ['message-list', channel] });
      // Refresh the channel list preview/unread immediately instead of waiting
      // for the trusted RECALL_MESSAGE PubNub round-trip (which may fail/delay).
      onMessageRecalled?.(channel);
      toastSuccess(messageStrings.recallMessageSuccess);
      setDeleteTargetTimetoken(null);
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? error.response?.data?.message || messageStrings.recallMessageFailed
          : messageStrings.recallMessageFailed;
      toastError(message);
    } finally {
      setIsRecallingMessage(false);
      endBusy();
    }
  }, [
    beginBusy,
    endBusy,
    channel,
    deleteTargetTimetoken,
    queryClient,
    onMessageRecalled,
    toastSuccess,
    toastError,
  ]);

  const onTyping = React.useCallback(
    (status: boolean) => {
      pubnub.signal({
        message: status ? SIGNAL_EVENT.TYPING_ON : SIGNAL_EVENT.TYPING_OFF,
        channel: channel,
      });
    },
    [pubnub, channel],
  );

  useEffect(() => {
    if (data?.pages) {
      const reverseData = [...data.pages[data.pages.length - 1]]
        .reverse()
        .filter((msg) => !isRecallMessageNotification(msg.message));
      setMessages((prev) => {
        const existing = new Set(
          prev.map((msg) => normalizePubNubTimetoken(msg.timetoken)),
        );
        const incoming = reverseData
          .map(mapMessage)
          .filter(
            (msg) => !existing.has(normalizePubNubTimetoken(msg.timetoken)),
          );
        return incoming.length > 0 ? [...prev, ...incoming] : prev;
      });
    }
  }, [data, mapMessage]);

  // Use refs to always have the latest callbacks without re-subscribing
  const handleMessageRef = React.useRef(handleMessage);
  const handleSignalRef = React.useRef(handleSignal);
  const handleFileRef = React.useRef(handleFile);
  const handleMessageActionRef = React.useRef(handleMessageAction);

  React.useEffect(() => {
    handleMessageRef.current = handleMessage;
    handleSignalRef.current = handleSignal;
    handleFileRef.current = handleFile;
    handleMessageActionRef.current = handleMessageAction;
  }, [handleMessage, handleSignal, handleFile, handleMessageAction]);

  useEffect(() => {
    if (
      !pubnub ||
      isLoading ||
      loading ||
      !channel ||
      subscribeChannels.length === 0
    ) {
      return;
    }

    const listenerParams = {
      message: (event: MessageEvent) => handleMessageRef.current(event),
      signal: (event: SignalEvent) => handleSignalRef.current(event),
      file: (event: FileEvent) => handleFileRef.current(event),
      messageAction: (event: {
        channel: string;
        event?: string;
        data?: { type?: string };
        messageTimetoken?: string | number;
      }) => handleMessageActionRef.current(event),
    };

    pubnub.addListener(listenerParams);
    pubnub.subscribe({ channels: subscribeChannels });

    return () => {
      pubnub.removeListener(listenerParams);
      // UUID チャネルは AppLayout と共有し、PubNub の購読は参照カウントしないため外さない
      pubnub.unsubscribe({ channels: [channel] });
    };
  }, [channel, pubnub, isLoading, loading, subscribeChannels]);

  useEffect(() => {
    // Clear timeout update channel active
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
        mutate();
      }
    };
  }, [mutate]);

  // Update seen time when channel is opened (only when channel changes)
  useEffect(() => {
    if (!channel || loading || isLoading) return;

    mutateSeen().catch(() => {});
  }, [channel, loading, isLoading, mutateSeen]);

  return (
    <div className="h-full flex flex-col justify-between overflow-hidden">
      <ChatHeader
        participants={participants}
        loading={loading || isLoading}
        onBack={onBack}
        responseStatus={responseStatus}
        onChangeResponseStatus={
          inquiryChatMode ? undefined : onChangeResponseStatus
        }
        isUpdatingResponseStatus={isUpdatingResponseStatus}
      />
      <MessageList
        isLoading={loading || isLoading}
        messages={messages}
        fetchMore={fetchNextPage}
        hasMore={hasNextPage}
        members={members}
        enableAi={enableAi}
        selectedTimetokens={inquiryAi.selectedTimetokens}
        onToggleSelect={inquiryAi.handleToggleSelect}
        onShowAiHistory={inquiryAi.handleShowAiHistory}
        inquiryChatMode={inquiryChatMode}
        canDelete
        onDeleteRequest={handleDeleteRequest}
      />
      <MessageInput
        channel={channel}
        sendMessage={sendMessage}
        onTyping={onTyping}
        typingList={memberIsTyping.map(
          (uuid) =>
            (members[uuid]?.name || adminAccounts[uuid]?.name || '') +
            commonStrings.honorificSuffix,
        )}
        loading={loading || isLoading}
        enableAi={enableAi}
        aiSelectedCount={inquiryAi.aiSelectedCount}
        aiLoading={inquiryAi.aiBatchLoading}
        onAiGenerate={inquiryAi.handleBatchGenerate}
      />
      {inquiryAi.aiModalOpen && (
        <AiSuggestionModal
          results={inquiryAi.aiModalResults}
          loading={inquiryAi.aiBatchLoading}
          onClose={inquiryAi.closeSuggestionModal}
          onRegenerate={inquiryAi.handleRegenerate}
        />
      )}

      {inquiryAi.aiHistoryOpen && (
        <AiHistoryModal
          history={inquiryAi.aiHistoryItems}
          loading={inquiryAi.aiHistoryLoading}
          onClose={inquiryAi.closeHistoryModal}
        />
      )}

      <ConfirmModal
        open={deleteTargetTimetoken !== null}
        textTitle={messageStrings.recallMessageConfirmTitle}
        textMessage={messageStrings.recallMessageConfirmMessage}
        textCancel={commonStrings.cancel}
        textConfirm={messageStrings.recallMessage}
        confirmButtonVariant="primary"
        confirmButtonClassName="!bg-red-500 hover:!bg-red-600"
        isLoading={isRecallingMessage}
        onClose={() => {
          if (!isRecallingMessage) setDeleteTargetTimetoken(null);
        }}
        onConfirm={handleConfirmRecall}
      />
    </div>
  );
}
